export function createSerializedSaveQueue<Request>(send: (request: Request) => Promise<void>, keyOf: (request: Request) => string = () => 'default') {
  const pending = new Map<string, Request>();
  const failed = new Map<string, Request>();
  const activeKeys = new Set<string>();
  let running: Promise<void> | undefined;

  function drain(): Promise<void> {
    if (running) return running;
    const work = async () => {
      let firstError: unknown;
      while (pending.size) {
        const [key, request] = pending.entries().next().value as [string, Request];
        pending.delete(key);
        activeKeys.add(key);
        try {
          await send(request);
        } catch (error) {
          if (pending.has(key)) continue;
          failed.set(key, request);
          firstError ??= error;
        } finally {
          activeKeys.delete(key);
        }
      }
      if (firstError) throw firstError;
    };
    let task: Promise<void>;
    task = work().finally(() => { if (running === task) running = undefined; });
    running = task;
    return task;
  }

  function enqueue(request: Request) {
    const key = keyOf(request);
    pending.set(key, request);
    failed.delete(key);
    return drain();
  }

  function flush() {
    for (const [key, request] of failed) {
      if (!pending.has(key)) pending.set(key, request);
      failed.delete(key);
    }
    return drain();
  }

  return {
    enqueue,
    flush,
    hasPending: (key?: string) => key === undefined ? pending.size > 0 || failed.size > 0 : pending.has(key) || failed.has(key),
    isSaving: (key?: string) => key === undefined ? running !== undefined : activeKeys.has(key),
    hasFailed: () => failed.size > 0
  };
}
